'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { EGrammarType } from '@quesmed/types';
import { EBatchType, EContentType, EEntitlementType, ETopicType } from '@quesmed/types/models';
import {
  type IValidateBookData,
  type IValidateBookVar,
  VALIDATE_BOOK,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { GrCloudUpload } from 'react-icons/gr';
import { z } from 'zod';
import {
  contentTypes,
  entitlementMapping,
  getOptions,
  grammarOptions,
  productOptions,
  topicOptions,
} from '@/config';
import { ErrorFeedback, Heading, SuccessFeedback } from '@/core/molecules';
import {
  Alert,
  AnimatedComponent,
  Button,
  cn,
  FormCheckBox,
  FormFileInput,
  FormInput,
  FormSelect,
  type FormSelectProps,
  FormTextArea,
} from '@/core/ui';
import { useMutation } from '@/libs';
import { useGlobal } from '@/store';
import { fileSchema, forcedChecksSchema, forcedInsertsSchema, overWriteSchema, stringToArr } from '@/utils';

const schema = z.object({
  folders: z.string().min(1, 'Folders are required'),
  entitlementId: z.enum(EEntitlementType, {
    message: 'Invalid enum value',
  }),
  contentType: z.enum(EContentType, { message: 'Invalid enum value' }),
  topicTypeId: z.enum(ETopicType, { message: 'Invalid enum value' }),
  grammarType: z.enum(EGrammarType, { message: 'Invalid enum value' }),
  forcedChecks: forcedChecksSchema.optional(),
  forcedInserts: forcedInsertsSchema.optional(),
  comment: z.string(),
  file: fileSchema,
  editMode: z.boolean().optional(),
  overWrite: overWriteSchema
    .nullable()
    .optional()
    .transform((val) => val ?? null),
});

type ISchema = z.input<typeof schema>;

const ValidateBook = () => {
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      comment: '',
      overWrite: null,
      contentType: EContentType.MARKDOWN,
      forcedChecks: {
        concept: false,
        question: false,
        station: false,
        topic: false,
        condition: false,
        presentation: false,
        ukmlaTopic: false,
        theme: false,
      },
      forcedInserts: {
        concept: false,
        question: false,
        station: false,
        topic: false,
        condition: false,
        presentation: false,
        ukmlaTopic: false,
        theme: false,
      },
    },
  });

  const dbType = useGlobal.use.dbType();

  const [bookValidate, { loading, data }] = useMutation<IValidateBookData, IValidateBookVar>(VALIDATE_BOOK);

  const finalData = useMemo(() => data?.admin?.validateBook, [data?.admin?.validateBook]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    try {
      delete input.editMode;

      const { error } = await bookValidate({
        variables: {
          input: { ...input, folders: stringToArr(input.folders) } as any,
        },
      });

      if (error) {
        throw new Error();
      }
      // resetField('file');
    } catch (_error) {
      // rese_errord('file');
    }
  };

  const handleCategoryChange: FormSelectProps<ISchema>['onValueChange'] = (value) => {
    const topicTypeId = value ? (entitlementMapping[value as EEntitlementType]?.typeId as ETopicType) : null;
    if (!topicTypeId) {
      return;
    }
    setValue('topicTypeId', topicTypeId);
  };

  const formData = useMemo(() => {
    return {
      topic: getOptions(topicOptions[EBatchType.CHAPTER], dbType),
      product: getOptions(productOptions[EBatchType.CHAPTER], dbType),
      grammar: getOptions(grammarOptions[EBatchType.CHAPTER], dbType),
    };
  }, [dbType]);

  return (
    <div className='flex flex-col space-y-2 justify-center w-full items-center'>
      <div className='bg-background rounded-lg shadow-md container z-[2]'>
        <Heading title='Validate Book' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
          <Alert
            type='warn'
            className='mb-8'
            message='Please read below before proceeding!'
            list={[
              'Make sure correct DB storage is selected',
              'Do not forgot to turn on overWrite mode in case of updating existing data',
              'By default, imported content will be hidden, you can make it visible under <i>`Content > Batch > Public`</i> menu',
              'If records are public, make sure to update algolia and wikipedia links, ask dev to do it as of now!',
              'If you are overwriting a chapter that already contains images and you would like to keep the existing images, on the images.csv your image should be a new index ie 2 (if 2 images already exist). If you want to replace an existing image, you can use the same index as the image you would like to replace.',
            ]}
          />
          <div className='grid grid-cols-8 items-start gap-y-6 md:gap-y-8 gap-x-8'>
            <FormTextArea
              isRequired={true}
              disableAutosize
              minRows={2}
              description='Enter comma separated folders eg:- Folder, Folder2'
              control={control}
              name='folders'
              labelPlacement='outside'
              placeholder='Folder1, Folder2'
              className='rounded col-span-8 lg:col-span-4'
              variant='faded'
              label='Folders'
            />
            <FormTextArea
              disableAutosize
              minRows={2}
              description='Comment/Information to store for this batch'
              control={control}
              name='comment'
              labelPlacement='outside'
              placeholder='Type Here..'
              className='rounded col-span-8 lg:col-span-4'
              variant='faded'
              label='Comment'
            />

            <FormSelect
              isRequired={true}
              description='Select category for product'
              sections={formData.product}
              options={[]}
              // description="This is basically type of product category"
              control={control}
              onValueChange={handleCategoryChange}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              name='entitlementId'
              placeholder='Select Product Category'
              className='rounded col-span-8 md:col-span-4 xl:col-span-2'
              variant='faded'
              label='Product Category'
            />
            <FormSelect
              isRequired={true}
              description=' '
              options={[]}
              sections={formData.topic}
              // description="This is basically type of topic"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              name='topicTypeId'
              labelPlacement='outside'
              placeholder='Select Topic Category'
              className='rounded col-span-8 md:col-span-4 xl:col-span-2'
              variant='faded'
              label='Topic Type'
            />

            <FormSelect
              isRequired={true}
              description=' '
              options={[]}
              sections={formData.grammar}
              // description="This is basically type of question"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Template'
              name='grammarType'
              className='rounded col-span-8 md:col-span-4 xl:col-span-2'
              variant='faded'
              label='Template Type'
            />

            <FormSelect
              isRequired={true}
              description='Select type'
              options={contentTypes}
              // description="This is basically type of question"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Input Content Type'
              name='contentType'
              className='rounded col-span-8 md:col-span-4 xl:col-span-2'
              variant='faded'
              label='Content Type'
            />
            <FormFileInput
              isRequired={true}
              description='Please upload only zip files'
              className='col-span-8 lg:col-span-4 h-full'
              control={control}
              name='file'
              label='Zip File'
            >
              <GrCloudUpload className='text-foreground w-14 h-14' />
            </FormFileInput>

            <div className='flex flex-col space-y-1 col-span-8 md:col-span-4 lg:col-span-2 w-full h-full'>
              <span className='text-sm text-foreground pb-1'>Force Actions</span>
              <div
                className={cn(
                  'flex flex-col justify-between rounded-md h-full',
                  'border-2 border-default-200 p-4',
                )}
              >
                <p className='text-danger text-sm mb-6'>
                  Use it when you want to force new insertion if they don't exist in db
                </p>

                <div className='flex flex-col'>
                  {/* <FormCheckBox control={control} name='forcedChecks.question'>
                    Force Book
                  </FormCheckBox> */}
                  <FormCheckBox
                    classNames={{ label: 'text-sm' }}
                    className='py-4'
                    control={control}
                    name='forcedChecks.topic'
                  >
                    Force Topic
                  </FormCheckBox>
                  <FormCheckBox
                    classNames={{ label: 'text-sm' }}
                    control={control}
                    name='forcedChecks.concept'
                  >
                    Force Concept
                  </FormCheckBox>
                </div>
              </div>
            </div>
            <div
              className={cn('flex flex-col space-y-1 col-span-8 md:col-span-4 lg:col-span-2 w-full h-full')}
            >
              <FormCheckBox
                classNames={{ label: 'text-foreground text-sm' }}
                name='editMode'
                size='sm'
                control={control}
                className='rounded'
              >
                Overwrite Data
              </FormCheckBox>
              <div className={cn('h-full rounded-md', 'border-2 border-default-200 p-4')}>
                {watch('editMode') ? (
                  <div className='flex flex-col h-full justify-between'>
                    <FormInput
                      // isRequired={true}
                      // description='Enter Previous BatchId'
                      control={control}
                      name='overWrite.batchId'
                      labelPlacement='outside'
                      placeholder='04fe9191-24da-47bc-80d5-61f5089337ab'
                      className='rounded'
                      variant='faded'
                      label='Batch Id'
                    />

                    <div className='flex flex-col'>
                      {/* <FormCheckBox
                        classNames={{ base: 'py-4', label: 'text-sm' }}
                        name='overWrite.resetProgress'
                        control={control}
                        className='rounded'
                      >
                        Reset Progress
                      </FormCheckBox> */}
                      {/* <FormCheckBox
                        disabled
                        isDisabled
                        name='overWrite.picture'
                        control={control}
                        className='rounded'
                      >
                        Overwrite Picture
                      </FormCheckBox> */}
                    </div>
                  </div>
                ) : (
                  <div className='text-sm text-danger'>
                    Enable this action only when you want to overwrite the existing chapters with new data
                    that is in zip file, Also make sure the chapter number specified are correct
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full container py-5',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData.status === 200 ? (
              <SuccessFeedback overWrite={watch('editMode')} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

export default ValidateBook;
